import { ReduxProductCategoryFilter } from "~/features/search/components/Filters/components/CategoryFilter/CategoryFilter";
import { SalesFilter } from "~/features/search/components/Filters/components/SalesFilter/SalesFilter";
import { RelevantFilter } from "~/features/search/components/Filters/components/RelevantFilter/RelevantFilter";
import { Box } from "~/common/mui";
import { TechFilterCategory } from "~/features/search/components/Filters/components/TechFilter/TechFilterCategory";
import { verticalStacker } from "~/styles/styledMixins";
import {
  type FacetFilterData,
  Filter,
  type FilterConfig,
  type FilterType,
  type TaxonomyFilterData,
  type TechValueFilterCategoryData,
} from "~/features/search/models/filterConfigModels";
import { FacetFilter } from "~/features/search/components/Filters/components/FacetFilter/FacetFilter";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect } from "react";
import { selectAppliedTechFilterValues } from "~/features/search/redux/searchSelectors";
import { applyAllFiltersAction } from "~/features/search/redux/actions";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { TechFilterResetContext } from "./TechFilterResetContext";

interface Props {
  filterConfig: FilterConfig;
  hideTechFilterButtons?: boolean;
  hideNonCategoryFilters?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods?: any;
  onSubmit?: (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => void;
}

export const SearchFilters = ({
  filterConfig,
  hideTechFilterButtons,
  hideNonCategoryFilters,
  methods: externalMethods,
  onSubmit: externalOnSubmit,
}: Props) => {
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);
  const dispatch = useDispatch();

  const internalMethods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  const methods = externalMethods || internalMethods;

  useEffect(() => {
    if (!externalMethods) {
      methods.reset();
    }
  }, [appliedTechFilterValues, methods, externalMethods]);

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    if (externalOnSubmit) {
      externalOnSubmit(data);
    } else {
      const flattenedData = flattenTechFilterFormData(data);
      dispatch(applyAllFiltersAction(flattenedData));
    }
  };

  const resetFilters = useCallback(
    (filterUids?: string[]) => {
      const currentValues = methods.getValues();

      // If specific UIDs provided, clear only those. Otherwise, get all tech filter UIDs from current values
      const uidsToReset = filterUids || Object.keys(currentValues);

      uidsToReset.forEach((uid) => {
        methods.setValue(`${uid}.min`, undefined, { shouldDirty: true, shouldTouch: true });
        methods.setValue(`${uid}.max`, undefined, { shouldDirty: true, shouldTouch: true });
      });

      // Submit the form to trigger the search
      methods.handleSubmit(onSubmit)();
    },
    [methods, onSubmit],
  );

  return (
    <TechFilterResetContext.Provider value={{ resetFilters }}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box sx={{ ...verticalStacker() }}>
            {filterConfig.map((item) => {
              return filterRenderMap[item.type]?.(item, { hideNonCategoryFilters, hideTechFilterButtons }) ?? null;
            })}
          </Box>
        </form>
      </FormProvider>
    </TechFilterResetContext.Provider>
  );
};

type Config = {
  hideTechFilterButtons?: boolean;
  hideNonCategoryFilters?: boolean;
};

const renderFilter = <T extends FilterConfig[number]>(
  filterType: FilterType,
  item: FilterConfig[number],
  config: Config,
  render: (item: T, config: Config) => React.ReactNode,
): React.ReactNode | null => (item.type === filterType ? render(item as T, config) : null);

const filterRenderMap: Record<FilterType, (item: FilterConfig[number], config: Config) => React.ReactNode | null> = {
  [Filter.RELEVANT]: (_, config) => (
    <Box key={"relevant"} hidden={config.hideNonCategoryFilters}>
      <RelevantFilter />
    </Box>
  ),
  [Filter.TECH_VALUE]: (item, config) =>
    renderFilter(Filter.TECH_VALUE, item, config, (data: TechValueFilterCategoryData, config) => (
      <TechFilterCategory
        key={data.facetId}
        data={data}
        hidden={config.hideNonCategoryFilters}
        hideTechFilterButtons={config.hideTechFilterButtons}
      />
    )),
  [Filter.FACET]: (item, config) =>
    renderFilter(Filter.FACET, item, config, (data: FacetFilterData, config) => (
      <FacetFilter key={data.code} data={data} hidden={config.hideNonCategoryFilters ?? false} />
    )),
  [Filter.TAXONOMY]: (item, config) =>
    renderFilter(Filter.TAXONOMY, item, config, (data: TaxonomyFilterData) => (
      <ReduxProductCategoryFilter
        key={"productGroup"}
        taxonomy={data.categories}
        initiallyExpanded={data.initiallyExpanded}
      />
    )),
  [Filter.SALES]: (_, config) => (
    <Box key={"salesFilter"} hidden={config.hideNonCategoryFilters ?? false}>
      <SalesFilter />
    </Box>
  ),
};
